/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/order/order-list/columns.tsx
 *
 * Only the data, columns and labels are ours. The screen-to-template mapping is
 * specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */
import type { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import { formatDate, formatMoney } from "../../lib/format";
import { money, type OrderSummary } from "../../lib/services/ordering";
import { getStatusBadge } from "@steelyard/theme";
import { Badge, Flex, Text } from "rizzui";
import { PiEyeDuotone as EyeIcon, PiListMagnifyingGlassDuotone as OrdersIcon, PiUserDuotone as CustomerIcon } from "react-icons/pi";
import { RowActions } from "../../components/table/row-actions";
import { customerPath, orderPath } from "../../lib/paths";
import { OrderStateMenu } from "../../components/orders/order-state-menu";

/**
 * Sizes are the template's own values (`screen-map.md`'s size cheat-sheet): 300 for the
 * name/customer identity cell, 140 for status, 150 for a short field.
 *
 * No selection column: no bulk action exists on this list (or anywhere in `apps/admin` today), so
 * the template's `select` column is deliberately omitted rather than wired to nothing.
 *
 * The State cell is where an order's state changes (`OrderStateMenu`: Approve, Move to…, Reject…,
 * Cancel…, under the detail page's own conditions). The row's `⋯` menu goes to the customer behind
 * the order. This app hard-deletes nothing, so there is no trash item.
 */
export const orderColumns = (
  mayWrite: boolean,
  mayApprove = false,
  mayOverride = false,
  mayAdvance = false,
): ColumnDef<OrderSummary>[] => [
  {
    // The order number IS the identity here, so no avatar: an initial of "DEMO-2026-0001" says
    // nothing. The two non-refusal flags sit under it rather than in a column that is empty on
    // almost every row.
    id: "order",
    accessorKey: "orderNumber",
    header: "Order",
    size: 280,
    cell: ({ row }) => (
      <Flex direction="col" align="start" gap="1">
        <Link to={orderPath(row.original.orderNumber)} className="font-semibold text-gray-900 hover:underline">
          {row.original.orderNumber}
        </Link>
        <Text className="text-xs text-gray-500">
          {row.original.customerName ?? "Unknown customer"}
        </Text>
        {row.original.shortOnStock || row.original.reservationPending ? (
          <Flex gap="1" className="flex-wrap">
            {row.original.shortOnStock ? <Badge size="sm" variant="flat" color="danger">Short on stock</Badge> : null}
            {row.original.reservationPending ? <Badge size="sm" variant="outline">Reservation pending</Badge> : null}
          </Flex>
        ) : null}
      </Flex>
    ),
  },
  {
    id: "placedAt",
    accessorKey: "placedAt",
    header: "Placed",
    size: 150,
    cell: ({ row }) => <Text className="text-gray-600">{formatDate(row.original.placedAt)}</Text>,
  },
  {
    id: "state",
    accessorKey: "state",
    header: "State",
    size: 200,
    // Read-only callers (the dashboard's pending-approvals tile) get the plain badge.
    cell: ({ row }) =>
      mayWrite || mayApprove || mayOverride || mayAdvance ? (
        <OrderStateMenu order={row.original} mayWrite={mayWrite} mayApprove={mayApprove} mayOverride={mayOverride} mayAdvance={mayAdvance} />
      ) : (
        getStatusBadge(row.original.state)
      ),
  },
  {
    id: "type",
    accessorKey: "orderType",
    header: "Type",
    size: 140,
    cell: ({ row }) => (
      <Badge variant="flat" color={row.original.orderType === "credit" ? "primary" : "secondary"} rounded="md">
        {row.original.orderType === "credit" ? "Credit · B2B" : "Prepaid · B2C"}
      </Badge>
    ),
  },
  {
    id: "total",
    // The API's own figure as a number, so the sort compares amounts, not strings. Nothing added up.
    accessorFn: (order) => money(order.grandTotal) ?? undefined,
    sortUndefined: "last",
    header: "Total",
    size: 150,
    meta: { align: "right" },
    cell: ({ row }) => <span className="font-semibold tabular-nums text-gray-900">{formatMoney(money(row.original.grandTotal))}</span>,
  },
  {
    id: "actions",
    header: "",
    size: 110,
    cell: ({ row }) => (
      <RowActions
        name={row.original.orderNumber}
        primary={{ label: "View order", icon: EyeIcon, to: orderPath(row.original.orderNumber) }}
        more={[
          { label: "Customer record", icon: CustomerIcon, to: customerPath(row.original.customerId) },
          { label: "Orders from this customer", icon: OrdersIcon, to: `/orders?customerId=${row.original.customerId}` },
        ]}
      />
    ),
  },
];
